import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import { X, Check, CreditCard, Sparkles } from 'lucide-react';
import { tokenTopUpProduct } from '../stripe-config';
import CheckoutOptions from './CheckoutOptions';
import { AnimatePresence, motion } from 'framer-motion';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredFeature: string;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose, requiredFeature }) => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  useOverlayState(isOpen, 'subscription-modal');

  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setShowCheckout(false);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handlePurchase = async () => {
    setShowCheckout(true);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          className="fixed sm:absolute left-1/2 -translate-x-1/2 sm:left-auto sm:right-0 sm:translate-x-0 top-20 sm:top-auto mt-0 sm:mt-2 w-[calc(100vw-2rem)] sm:w-[420px] max-w-[420px] overflow-hidden rounded-xl border border-theme-cards bg-theme-cards shadow-2xl z-50"
        >
          <div className="p-4 sm:p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white">Get More Credits</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-theme-text-secondary hover:text-white hover:bg-gray-800/50 transition-colors"
              aria-label="Close"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {success ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-green-500" />
              </div>
              <h3 className="text-base font-semibold mb-2 text-white">Success!</h3>
              <p className="text-theme-text-secondary text-sm">
                Your credits have been added to your account.
              </p>
            </div>
          ) : showCheckout ? (
            <CheckoutOptions
              priceId={tokenTopUpProduct.priceId}
              mode="payment"
              amount={tokenTopUpProduct.price}
              productName={tokenTopUpProduct.name}
              accessToken={accessToken}
              successUrl={`${window.location.origin}/subscription-success?type=credits`}
              cancelUrl={`${window.location.origin}/subscription-cancelled`}
              metadata={{ type: 'token_topup' }}
              onSuccess={() => {
                setSuccess(true);
                setTimeout(() => {
                  onClose();
                }, 3000);
              }}
              onCancel={() => {
                setShowCheckout(false);
              }}
            />
          ) : (
            <>
              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <div className="text-theme-text-secondary font-medium mb-1">Monthly Allowance</div>
                  <div className="text-lg font-bold text-white">3 Vybes</div>
                  <div className="text-xs text-theme-text-secondary mt-1">Host up to 3 Vybes per month</div>
                </div>
              </div>

              <div className="space-y-3">
                <div
                  className="border rounded-lg p-4 border-amber-500 bg-amber-500/10 ring-2 ring-amber-500/50"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-white text-sm flex items-center gap-2">
                        <Sparkles size={14} className="text-amber-400" />
                        Credit Top-Up
                      </h3>
                      <p className="text-theme-text-secondary text-xs">Credits never expire</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">£{tokenTopUpProduct.price}</p>
                      <p className="text-[10px] text-theme-text-secondary">one-time</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 text-xs text-theme-text-secondary">
                    <li className="flex items-start">
                      <Check size={12} className="text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>+{tokenTopUpProduct.tokens.create} Vybe credits</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={12} className="text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>No monthly commitment</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={12} className="text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Credits roll over each month</span>
                    </li>
                  </ul>
                </div>
              </div>

              <button
                onClick={handlePurchase}
                disabled={isProcessing}
                className="w-full mt-4 py-2.5 rounded-full transition-colors flex items-center justify-center text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                type="button"
              >
                {isProcessing ? (
                  <span className="flex items-center">
                    <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Processing...
                  </span>
                ) : (
                  <>
                    <CreditCard size={16} className="mr-2" />
                    Purchase Credits
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  onClose();
                  navigate('/my-events');
                }}
                className="w-full mt-3 px-4 py-2.5 rounded-full text-sm font-semibold border border-gray-600 hover:border-gray-500 text-white hover:bg-gray-800/50 transition-all flex items-center justify-center gap-2"
                type="button"
              >
                Start a Vybe
              </button>
            </>
          )}
        </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubscriptionModal;
